import { NavLink, useLocation } from 'react-router-dom';
import { 
  FiGrid, FiPackage, FiTag, FiShoppingBag,
  FiBarChart2, FiBell, FiActivity,
  FiUsers, FiHome, FiLogOut, FiX, FiPercent, FiImage
} from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

const AdminSidebar = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: FiGrid, label: 'Dashboard' },
    { path: '/admin/products', icon: FiPackage, label: 'Products' },
    { path: '/admin/categories', icon: FiTag, label: 'Categories' },
    { path: '/admin/hero', icon: FiImage, label: 'Homepage Hero' },
    { path: '/admin/coupons', icon: FiPercent, label: 'Coupons' },
    { path: '/admin/orders', icon: FiShoppingBag, label: 'Orders' },
    { path: '/admin/analytics', icon: FiBarChart2, label: 'Sales Analytics' },
    { path: '/admin/notifications', icon: FiBell, label: 'Notifications' },
    { path: '/admin/activity', icon: FiActivity, label: 'Activity Logs' },
    { path: '/admin/users', icon: FiUsers, label: 'Users' },
  ];

  return (
    <>
      {/* Backdrop overlay for mobile */}
      <div 
        className={`admin-sidebar-overlay ${isOpen ? 'active' : ''}`}
        onClick={onClose}
      />
      
      <aside className={`admin-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Header with branding */}
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-brand">
            <h2>Tamarind Leaf</h2>
            <span className="admin-badge">Admin</span>
          </div>
          <button 
            className="admin-sidebar-close"
            onClick={onClose}
            aria-label="Close menu"
          >
            <FiX />
          </button>
        </div>

        {/* User info on mobile */}
        <div className="admin-sidebar-user">
          <div className="admin-sidebar-avatar">
            {user?.avatar ? (
              <img src={user.avatar} alt={user.name} />
            ) : (
              <span>{user?.name?.charAt(0) || 'A'}</span>
            )}
          </div>
          <div className="admin-sidebar-userinfo">
            <span className="admin-sidebar-username">{user?.name || 'Admin'}</span>
            <span className="admin-sidebar-role">Administrator</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="admin-sidebar-nav">
          <ul>
            {menuItems.map((item) => (
              <li key={item.path}>
                <NavLink 
                  to={item.path}
                  className={({ isActive }) => 
                    `admin-sidebar-link ${isActive ? 'active' : ''}`
                  }
                >
                  <item.icon className="admin-sidebar-icon" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer actions */}
        <div className="admin-sidebar-footer">
          <NavLink to="/" className="admin-sidebar-link">
            <FiHome className="admin-sidebar-icon" />
            <span>View Store</span>
          </NavLink>
          <button className="admin-sidebar-link" onClick={handleLogout}>
            <FiLogOut className="admin-sidebar-icon" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        confirmVariant="warning"
        onCancel={() => setShowLogoutConfirm(false)}
        onConfirm={async () => {
          setShowLogoutConfirm(false);
          await dispatch(logout());
          navigate('/login');
        }}
      />
    </>
  );
};

export default AdminSidebar;
